import { useEffect, useMemo, useRef, useState } from "react";
import { HiChevronDown, HiSearch } from "react-icons/hi";
import type { ExportOption } from "../../types/exports";
import { usePopover } from "./usePopover";

const PAGE_SIZE = 100;

/** Label with provider, plus the ID when two options would otherwise read the same. */
export function useOptionLabels(options: ExportOption[]) {
  return useMemo(() => {
    const key = (option: ExportOption) =>
      `${option.label}\u0000${option.provider ?? ""}`;
    const counts = new Map<string, number>();
    options.forEach((option) =>
      counts.set(key(option), (counts.get(key(option)) ?? 0) + 1)
    );
    const labels = new Map<number, string>();
    options.forEach((option) =>
      labels.set(
        option.id,
        `${option.label}${option.provider ? ` · ${option.provider}` : ""}${
          (counts.get(key(option)) ?? 0) > 1 ? ` · Nr. ${option.id}` : ""
        }`
      )
    );
    return labels;
  }, [options]);
}

interface Props {
  id: string;
  label: string;
  placeholder: string;
  options: ExportOption[];
  selected: number[];
  onChange: (next: number[]) => void;
  disabled?: boolean;
  loading: boolean;
  error?: string;
}

export function ExportOptionSelect({
  id,
  label,
  placeholder,
  options,
  selected,
  onChange,
  disabled,
  loading,
  error,
}: Props) {
  const popover = usePopover();
  const searchRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const labels = useOptionLabels(options);
  const normalized = query.trim().toLocaleLowerCase("de-DE");
  useEffect(() => setVisibleCount(PAGE_SIZE), [normalized]);
  useEffect(() => {
    if (popover.open) searchRef.current?.focus();
  }, [popover.open]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const matching = useMemo(
    () =>
      options.filter((option) =>
        `${option.label} ${option.provider ?? ""} ${option.id}`
          .toLocaleLowerCase("de-DE")
          .includes(normalized)
      ),
    [options, normalized]
  );
  const visible = matching.slice(0, visibleCount);
  const selectedOptions = options.filter((option) =>
    selectedSet.has(option.id)
  );
  const allMatchingSelected =
    matching.length > 0 &&
    matching.every((option) => selectedSet.has(option.id));

  const toggle = (optionId: number) =>
    onChange(
      selectedSet.has(optionId)
        ? selected.filter((value) => value !== optionId)
        : [...selected, optionId]
    );
  const toggleMatching = () => {
    const ids = new Set(matching.map((option) => option.id));
    onChange(
      allMatchingSelected
        ? selected.filter((value) => !ids.has(value))
        : [...new Set([...selected, ...ids])]
    );
  };

  const listId = `${id}-list`;

  return (
    <div
      ref={popover.containerRef}
      className="relative min-w-0 rounded-[inherit]"
    >
      <button
        ref={popover.triggerRef}
        id={id}
        type="button"
        onClick={popover.toggle}
        disabled={disabled}
        aria-expanded={popover.open}
        aria-haspopup="dialog"
        aria-controls={popover.open ? listId : undefined}
        aria-label={`${label}: ${
          selectedOptions.length
            ? `${selectedOptions.length} ausgewählt`
            : placeholder
        }`}
        className="flex h-9 w-full min-w-0 items-center gap-1 rounded-[inherit] bg-white px-2.5 text-left text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400 dark:bg-gray-700 dark:text-white dark:disabled:bg-gray-800 dark:disabled:text-gray-500"
      >
        <span className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
          {selectedOptions.length === 0 ? (
            <span className="truncate text-gray-500 dark:text-gray-400">
              {loading ? "Wird geladen …" : placeholder}
            </span>
          ) : (
            <>
              {selectedOptions.slice(0, 2).map((option) => (
                <span
                  key={option.id}
                  className="max-w-[10rem] shrink truncate rounded bg-blue-50 px-1.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                >
                  {labels.get(option.id)}
                </span>
              ))}
              {selectedOptions.length > 2 && (
                <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-600 dark:text-gray-200">
                  +{(selectedOptions.length - 2).toLocaleString("de-DE")}
                </span>
              )}
            </>
          )}
        </span>
        <HiChevronDown
          aria-hidden="true"
          className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${
            popover.open ? "rotate-180" : ""
          }`}
        />
      </button>

      {popover.open && (
        <div
          id={listId}
          role="dialog"
          aria-label={`${label} auswählen`}
          className="relative z-40 mt-1 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-700 sm:absolute sm:left-0 sm:top-full sm:w-80"
        >
          <div className="border-b border-gray-200 p-2 dark:border-gray-600">
            <div className="relative">
              <HiSearch
                aria-hidden="true"
                className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400"
              />
              <label htmlFor={`${id}-search`} className="sr-only">
                {label} durchsuchen
              </label>
              <input
                ref={searchRef}
                id={`${id}-search`}
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Suchen …"
                autoComplete="off"
                className="w-full rounded-md border border-gray-300 py-1.5 pl-8 pr-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-600 dark:text-white"
              />
            </div>
          </div>

          {loading && (
            <p className="px-3 py-2 text-sm text-gray-500" role="status">
              Optionen werden geladen …
            </p>
          )}
          {error && (
            <p
              className="px-3 py-2 text-sm text-red-700 dark:text-red-300"
              role="alert"
            >
              {error}
            </p>
          )}
          {!loading && !error && (
            <>
              <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-2 py-1 text-xs dark:border-gray-600">
                <button
                  type="button"
                  onClick={toggleMatching}
                  disabled={matching.length === 0}
                  className="rounded px-1.5 py-1 font-medium text-blue-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 dark:text-blue-300 dark:hover:bg-gray-600"
                >
                  {allMatchingSelected
                    ? normalized
                      ? "Treffer abwählen"
                      : "Alle abwählen"
                    : normalized
                    ? `Alle ${matching.length.toLocaleString(
                        "de-DE"
                      )} Treffer auswählen`
                    : "Alle auswählen"}
                </button>
                {selected.length > 0 && (
                  <button
                    type="button"
                    onClick={() => onChange([])}
                    className="rounded px-1.5 py-1 text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-300 dark:hover:bg-gray-600"
                  >
                    Auswahl leeren ({selected.length.toLocaleString("de-DE")})
                  </button>
                )}
              </div>
              <div
                className="max-h-60 overflow-y-auto p-1"
                role="group"
                aria-label={label}
              >
                {matching.length === 0 && (
                  <p className="p-2 text-center text-sm text-gray-500">
                    Keine Treffer
                  </p>
                )}
                {visible.map((option) => (
                  <label
                    key={option.id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-gray-800 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-600"
                  >
                    <input
                      type="checkbox"
                      checked={selectedSet.has(option.id)}
                      onChange={() => toggle(option.id)}
                      className="h-3.5 w-3.5 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-500 dark:bg-gray-600"
                    />
                    <span className="min-w-0 break-words">
                      {labels.get(option.id)}
                    </span>
                    {!option.active && (
                      <span className="ml-auto shrink-0 text-xs text-gray-500 dark:text-gray-400">
                        inaktiv
                      </span>
                    )}
                  </label>
                ))}
                {matching.length > visible.length && (
                  <button
                    type="button"
                    onClick={() =>
                      setVisibleCount((count) => count + PAGE_SIZE)
                    }
                    className="mt-1 w-full rounded px-2 py-1.5 text-left text-xs font-medium text-blue-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-blue-300 dark:hover:bg-gray-600"
                  >
                    Weitere anzeigen ({visible.length.toLocaleString("de-DE")}{" "}
                    von {matching.length.toLocaleString("de-DE")})
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
